import { Icon } from '@ministries/storybook'
import {
  Form,
  Link,
  useLocation,
  useMatches,
  useRouteLoaderData
} from 'react-router'

import LanguageSwitcher from '../i18n/LanguageSwitcher'

import { useI18n } from '@app/utilities/i18n/provider'

import {
  getAppNavItems,
  getAppNavTarget,
  isAppNavItemActive
} from './Navigation'

interface AppDesktopRootLoaderData {
  currentTenant: {
    name: string
    role: string
  } | null
}

interface AppDesktopCurrentUserData {
  currentUser: {
    role: string
  }
}

function isAppDesktopRootLoaderData(
  value: unknown
): value is AppDesktopRootLoaderData {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  return 'currentTenant' in value
}

function isAppDesktopCurrentUserData(
  value: unknown
): value is AppDesktopCurrentUserData {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const record = value as Record<string, unknown>

  if (typeof record.currentUser !== 'object' || record.currentUser === null) {
    return false
  }

  const currentUser = record.currentUser as Record<string, unknown>

  return typeof currentUser.role === 'string'
}

export default function Desktop() {
  const { hash, pathname } = useLocation()
  const matches = useMatches()
  const rootLoaderData: unknown = useRouteLoaderData('root')
  const currentTenant = isAppDesktopRootLoaderData(rootLoaderData)
    ? rootLoaderData.currentTenant
    : null

  const { t } = useI18n()
  const isSignInRoute = pathname === '/sign-in'
  const isTenantSelectRoute = pathname === '/tenant-select'
  const currentUser = matches
    .map(match => match.data)
    .find(isAppDesktopCurrentUserData)?.currentUser

  const navItems = getAppNavItems(
    {
      admin: t('home.navigation.admin'),
      coffee: t('home.navigation.coffee'),
      events: t('home.navigation.events'),
      features: t('home.navigation.features'),
      home: t('home.navigation.home'),
      music: t('home.navigation.music'),
      notifications: t('home.navigation.notifications'),
      profile: t('home.navigation.profile')
    },
    {
      isAdmin:
        currentTenant?.role === 'owner' ||
        currentTenant?.role === 'admin' ||
        currentUser?.role === 'admin'
    }
  )

  return (
    <nav
      aria-label="App navigation"
      className="sticky top-0 z-30 hidden border-b border-zinc-200/80 bg-zinc-100/90 backdrop-blur md:block"
    >
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-6 px-4 py-4 lg:px-6">
        <Link
          to="/"
          className="shrink-0 text-xs font-semibold uppercase tracking-[0.26em] text-zinc-500 transition hover:text-zinc-950"
        >
          {t('app.title')}
        </Link>

        <div className="flex flex-1 items-center justify-end gap-4">
          {isSignInRoute || isTenantSelectRoute ? null : (
            <ul className="flex flex-1 flex-wrap items-center justify-end gap-2">
              {navItems.map(item => {
                const isActive = isAppNavItemActive(item, pathname, hash)

                return (
                  <li key={item.id}>
                    <Link
                      to={getAppNavTarget(item)}
                      className={[
                        'flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition',
                        isActive
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 shadow-[0_8px_24px_rgba(16,185,129,0.12)]'
                          : 'border-transparent bg-white/85 text-zinc-600 hover:border-zinc-200 hover:text-zinc-950'
                      ].join(' ')}
                    >
                      <Icon
                        name={item.icon}
                        className={
                          isActive
                            ? 'h-4 w-4 text-emerald-600'
                            : 'h-4 w-4 text-zinc-500'
                        }
                      />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
          {currentTenant ? (
            <Link
              to="/tenant-select?switch=1"
              className="hidden rounded-full border border-zinc-200 bg-white/85 px-3 py-2 text-right text-xs text-zinc-500 transition hover:border-emerald-300 hover:bg-emerald-50 lg:block"
            >
              <div className="font-semibold uppercase tracking-[0.16em] text-zinc-700">
                {currentTenant.name}
              </div>
              <div>{currentTenant.role}</div>
            </Link>
          ) : null}
          {isSignInRoute || isTenantSelectRoute ? null : (
            <Form action="/" method="post">
              <input type="hidden" name="intent" value="sign_out" />
              <button
                type="submit"
                className="rounded-full border border-zinc-200 bg-white/85 px-4 py-2 text-sm font-medium text-zinc-600 transition hover:border-zinc-300 hover:text-zinc-950"
              >
                {t('planning.sign_out')}
              </button>
            </Form>
          )}
          <LanguageSwitcher />
        </div>
      </div>
    </nav>
  )
}
